import React, {useState, useEffect, useMemo} from 'react';
import {MediaPopup, MediaApiService} from '@tradly/asset';
import AppConstants from '../Constants/AppConstants';
import networkService from '../Network/NetworkManager';
import PropTypes from 'prop-types';

/**
 * TradlyMediaPicker - A reusable component for uploading and selecting media
 * with automatic Tradly authentication configuration
 *
 * @example
 * <TradlyMediaPicker
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSelect={(mediaUrl) => console.log('Selected:', mediaUrl)}
 *   options={['image', 'video']}
 * />
 */
const TradlyMediaPicker = ({
  // Controlled props (optional)
  isOpen,
  onClose,

  // Core behavior
  onSelect,
  currentData,
  options = ['image'],
  onError,
  title = 'Media Gallery',

  // Uncontrolled mode (component manages its own open/close)
  defaultOpen = false,
  renderTrigger, // (open) => ReactNode – optional trigger renderer

  // Styling props
  overlayClassName,
  containerClassName,
  headerClassName,
  titleClassName,
  closeButtonClassName,
  tabListClassName,
  tabButtonClassName,
  tabButtonActiveClassName,
  tabButtonInactiveClassName,
  gridClassName,
  imageItemClassName,
  videoItemClassName,
  paginationContainerClassName,
}) => {
  const isControlled = typeof isOpen === 'boolean';
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const actualOpen = isControlled ? isOpen : internalOpen;

  const open = () => {
    if (!isControlled) {
      setInternalOpen(true);
    }
  };

  const handleClose = () => {
    if (!isControlled) {
      setInternalOpen(false);
    }
    if (onClose) {
      onClose();
    }
  };

  // Initialize API service with automatic auth configuration
  const apiService = useMemo(() => {
    // Get auth key from NetworkManager
    const authKey = networkService.getAuthKey();

    // Get bearer token from AppConstants
    const bearerToken = AppConstants.BearerAuthorization;

    // Determine environment
    const environment = AppConstants.isDevelopment ? 'dev' : 'production';

    // Get base URL - remove trailing slash if present
    let apiBaseUrl = AppConstants.BaseURL;
    if (apiBaseUrl && apiBaseUrl.endsWith('/')) {
      apiBaseUrl = apiBaseUrl.slice(0, -1);
    }

    // Create and configure API service
    const service = new MediaApiService({
      authKey: authKey || '',
      bearerToken: bearerToken || '',
      environment: environment,
      apiBaseUrl: apiBaseUrl,
      onError: error => {
        console.error('TradlyMediaPicker API Error:', error);
        if (onError) {
          onError(error);
        }
      },
    });

    return service;
  }, [onError]);

  // Update API service when auth tokens change
  useEffect(() => {
    if (apiService) {
      const authKey = networkService.getAuthKey();
      const bearerToken = AppConstants.BearerAuthorization;

      if (authKey) {
        apiService.setAuthKey(authKey);
      }

      if (bearerToken) {
        apiService.setBearerToken(bearerToken);
      }
    }
  }, [apiService, actualOpen]);

  const handleSelect = mediaUrl => {
    if (onSelect) {
      onSelect(mediaUrl);
    }
  };

  const handleError = error => {
    console.error('TradlyMediaPicker Error:', error);
    if (onError) {
      onError(error);
    }
  };

  const popup = actualOpen ? (
    <MediaPopup
      isOpen={actualOpen}
      onClose={handleClose}
      onSelect={handleSelect}
      currentData={currentData}
      options={options}
      apiService={apiService}
      onError={handleError}
      title={title}
      // Pass through styling props
      overlayClassName={overlayClassName}
      containerClassName={containerClassName}
      headerClassName={headerClassName}
      titleClassName={titleClassName}
      closeButtonClassName={closeButtonClassName}
      tabListClassName={tabListClassName}
      tabButtonClassName={tabButtonClassName}
      tabButtonActiveClassName={tabButtonActiveClassName}
      tabButtonInactiveClassName={tabButtonInactiveClassName}
      gridClassName={gridClassName}
      imageItemClassName={imageItemClassName}
      videoItemClassName={videoItemClassName}
      paginationContainerClassName={paginationContainerClassName}
    />
  ) : null;

  // If a trigger is provided, let the component handle open/close itself
  if (renderTrigger) {
    return (
      <>
        {renderTrigger(open)}
        {popup}
      </>
    );
  }

  // Fallback: controlled mode without trigger
  return popup;
};

TradlyMediaPicker.propTypes = {
  /** Controls popup visibility (optional). If omitted, component manages its own state when `renderTrigger` is used. */
  isOpen: PropTypes.bool,
  /** Callback when popup closes */
  onClose: PropTypes.func,
  /** Callback when media is selected - receives the media URL */
  onSelect: PropTypes.func.isRequired,
  /** Currently selected media data */
  currentData: PropTypes.any,
  /** Media types to show: 'image', 'video', 'file' */
  options: PropTypes.arrayOf(PropTypes.oneOf(['image', 'video', 'file'])),
  /** Error handler callback */
  onError: PropTypes.func,
  /** Popup title */
  title: PropTypes.string,
  /** Initial open state in uncontrolled mode */
  defaultOpen: PropTypes.bool,
  /** Optional trigger renderer: (open) => ReactNode. When provided, component handles open/close internally. */
  renderTrigger: PropTypes.func,
  // Styling props
  overlayClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  titleClassName: PropTypes.string,
  closeButtonClassName: PropTypes.string,
  tabListClassName: PropTypes.string,
  tabButtonClassName: PropTypes.string,
  tabButtonActiveClassName: PropTypes.string,
  tabButtonInactiveClassName: PropTypes.string,
  gridClassName: PropTypes.string,
  imageItemClassName: PropTypes.string,
  videoItemClassName: PropTypes.string,
  paginationContainerClassName: PropTypes.string,
};

export default TradlyMediaPicker;
